// src/useDataPolling.ts
import { assetsActions as assetsActions7, useAppDispatch as useAppDispatch12 } from "@umami/state";
import { fromUnixTime } from "date-fns";
import max from "lodash/max";
import { useEffect as useEffect11 } from "react";

// src/usePollAccountStates.ts
import { useQuery } from "@tanstack/react-query";
import { ChainFullIds } from "@umami/chains";
import { assetsActions, useAllAccounts, useAppDispatch as useAppDispatch2, useSelectedNetwork } from "@umami/state";
import { BLOCK_TIME } from "@umami/tezos";
import { getAccounts } from "@umami/tzkt";
import { useEffect } from "react";

// src/useReactQueryErrorHandler.ts
import { useToast } from "@chakra-ui/react";
import { errorsActions, useAppDispatch } from "@umami/state";
import { getErrorContext } from "@umami/utils";
import { useCallback } from "react";
var useReactQueryErrorHandler = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const toastId = "data-fetching-error";
  return useCallback(
    (error) => {
      if (!error) {
        return;
      }
      dispatch(errorsActions.add(getErrorContext(error)));
      const context = getErrorContext(error);
      if (!toast.isActive(toastId)) {
        toast({
          id: toastId,
          description: `Data fetching error: ${context.description}`,
          status: "error",
          isClosable: true,
          duration: 1e4
        });
      }
      console.error(error);
    },
    [dispatch, toast]
  );
};

// src/useRefetchTrigger.ts
import { useAppSelector } from "@umami/state";
var useRefetchTrigger = () => useAppSelector((state) => state.assets.refetchTrigger);

// src/usePollAccountStates.ts
var usePollAccountStates = () => {
  const dispatch = useAppDispatch2();
  const handleError = useReactQueryErrorHandler();
  const network = useSelectedNetwork();
  const refetchTrigger = useRefetchTrigger();
  const addresses = useAllAccounts().filter((account) => account.chains?.[ChainFullIds.TEZOS]).map((account) => account.address.pkh);
  const query = useQuery({
    queryKey: ["accountStates", network, addresses, refetchTrigger],
    queryFn: () => getAccounts(addresses, network),
    retry: false,
    // retries are handled by the underlying functions
    refetchInterval: network.blockTime ?? BLOCK_TIME,
    select: (accountStates2) => accountStates2.flat(),
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false
  });
  const accountStates = query.data;
  useEffect(() => {
    accountStates && dispatch(assetsActions.updateAccountStates(accountStates));
  }, [dispatch, accountStates]);
  handleError(query.error);
  return query;
};

// src/usePollBakers.ts
import { useQuery as useQuery2 } from "@tanstack/react-query";
import { assetsActions as assetsActions2, useAppDispatch as useAppDispatch3, useSelectedNetwork as useSelectedNetwork2 } from "@umami/state";
import { getBakers } from "@umami/tzkt";
import { hoursToMilliseconds } from "date-fns";
import { useEffect as useEffect2 } from "react";
var BAKERS_REFRESH_RATE = hoursToMilliseconds(2);
var usePollBakers = () => {
  const dispatch = useAppDispatch3();
  const handleError = useReactQueryErrorHandler();
  const network = useSelectedNetwork2();
  const query = useQuery2({
    queryKey: ["bakers", dispatch, network],
    queryFn: () => getBakers(network),
    retry: false,
    // retries are handled by the underlying functions
    refetchInterval: BAKERS_REFRESH_RATE,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false
  });
  const bakers = query.data;
  useEffect2(() => {
    bakers && dispatch(assetsActions2.updateBakers(bakers));
  }, [dispatch, bakers]);
  handleError(query.error);
  return query;
};

// src/usePollBlock.ts
import { useQuery as useQuery3 } from "@tanstack/react-query";
import { assetsActions as assetsActions3, useAppDispatch as useAppDispatch4, useSelectedNetwork as useSelectedNetwork3 } from "@umami/state";
import { BLOCK_TIME as BLOCK_TIME2 } from "@umami/tezos";
import { getLatestBlock } from "@umami/tzkt";
import { useEffect as useEffect3 } from "react";
var usePollBlock = () => {
  const dispatch = useAppDispatch4();
  const handleError = useReactQueryErrorHandler();
  const network = useSelectedNetwork3();
  const refetchTrigger = useRefetchTrigger();
  const query = useQuery3({
    queryKey: ["latestBlock", dispatch, network, refetchTrigger],
    queryFn: () => getLatestBlock(network),
    retry: false,
    // retries are handled by the underlying functions
    refetchInterval: network.blockTime ?? BLOCK_TIME2,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false
  });
  const block = query.data;
  useEffect3(() => {
    block && dispatch(assetsActions3.updateBlock(block));
  }, [dispatch, block]);
  handleError(query.error);
  return query;
};

// src/usePollConversionRate.ts
import { useQuery as useQuery4 } from "@tanstack/react-query";
import { assetsActions as assetsActions4, useAppDispatch as useAppDispatch5 } from "@umami/state";
import { getTezosPriceInUSD } from "@umami/tzkt";
import { minutesToMilliseconds } from "date-fns";
import { useEffect as useEffect4 } from "react";
var CONVERSION_RATE_REFRESH_RATE = minutesToMilliseconds(5);
var usePollConversionRate = () => {
  const dispatch = useAppDispatch5();
  const handleError = useReactQueryErrorHandler();
  const query = useQuery4({
    queryKey: ["conversionRate", dispatch],
    // eslint-disable-next-line @tanstack/query/no-void-query-fn -- getTezosPriceInUSD returns Promise<number | undefined> (quote.usd may be undefined); undefined is stored in state as-is
    queryFn: getTezosPriceInUSD,
    refetchInterval: CONVERSION_RATE_REFRESH_RATE,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false
  });
  const rate = query.data;
  useEffect4(() => {
    dispatch(assetsActions4.updateConversionRate(rate));
  }, [dispatch, rate]);
  handleError(query.error);
  return query;
};

// src/usePollMultisigs.ts
import { useQuery as useQuery5 } from "@tanstack/react-query";
import { ChainFullIds as ChainFullIds2 } from "@umami/chains";
import { getRelevantMultisigContracts } from "@umami/multisig";
import {
  multisigsActions,
  useAppDispatch as useAppDispatch6,
  useImplicitAccounts,
  useSelectedNetwork as useSelectedNetwork4
} from "@umami/state";
import { useEffect as useEffect5 } from "react";
var usePollMultisigs = () => {
  const dispatch = useAppDispatch6();
  const handleError = useReactQueryErrorHandler();
  const refetchTrigger = useRefetchTrigger();
  const network = useSelectedNetwork4();
  const addresses = useImplicitAccounts().filter((account) => account.chains?.[ChainFullIds2.TEZOS]).map((account) => account.address.pkh);
  const query = useQuery5({
    queryKey: ["multisigs", network, addresses, refetchTrigger],
    queryFn: () => getRelevantMultisigContracts(new Set(addresses), network),
    retry: false,
    // retries are handled by the underlying functions
    // TODO: This refetch impacts app performance and should be refactored for better efficiency.
    // uncomment this when we need multisigs again
    // refetchInterval: BLOCK_TIME,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false
  });
  const multisigs = query.data;
  useEffect5(() => {
    multisigs && dispatch(multisigsActions.setMultisigs(multisigs));
  }, [multisigs, dispatch]);
  handleError(query.error);
  return query;
};

// src/usePollPendingOperations.ts
import { useQuery as useQuery6 } from "@tanstack/react-query";
import { getPendingOperations } from "@umami/multisig";
import {
  multisigsActions as multisigsActions2,
  useAppDispatch as useAppDispatch7,
  useMultisigAccounts,
  useSelectedNetwork as useSelectedNetwork5
} from "@umami/state";
import { BLOCK_TIME as BLOCK_TIME3 } from "@umami/tezos";
import { useEffect as useEffect6 } from "react";
var usePollPendingOperations = () => {
  const dispatch = useAppDispatch7();
  const handleError = useReactQueryErrorHandler();
  const network = useSelectedNetwork5();
  const refetchTrigger = useRefetchTrigger();
  const multisigs = useMultisigAccounts();
  const query = useQuery6({
    queryKey: ["pendingOperations", network, multisigs, refetchTrigger],
    queryFn: () => getPendingOperations(multisigs, network),
    retry: false,
    // retries are handled by the underlying functions
    refetchInterval: network.blockTime ?? BLOCK_TIME3,
    select: (operations) => operations.flat(),
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false
  });
  const pendingOperations = query.data;
  useEffect6(() => {
    pendingOperations && dispatch(multisigsActions2.setPendingOperations(pendingOperations));
  }, [dispatch, pendingOperations]);
  handleError(query.error);
  return query;
};

// src/usePollProtocolSettings.ts
import { useQuery as useQuery7 } from "@tanstack/react-query";
import { protocolSettingsActions, useAppDispatch as useAppDispatch8, useSelectedNetwork as useSelectedNetwork6 } from "@umami/state";
import { getProtocolSettings } from "@umami/tezos";
import { hoursToMilliseconds as hoursToMilliseconds2 } from "date-fns";
import { useEffect as useEffect7 } from "react";
var usePollProtocolSettings = () => {
  const dispatch = useAppDispatch8();
  const handleError = useReactQueryErrorHandler();
  const network = useSelectedNetwork6();
  const query = useQuery7({
    queryKey: ["protocolSettings", network],
    queryFn: () => getProtocolSettings(network),
    refetchInterval: hoursToMilliseconds2(24),
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false
  });
  const settings = query.data;
  useEffect7(() => {
    if (settings) {
      dispatch(
        protocolSettingsActions.update({
          network,
          settings: {
            maxSlashingPeriod: settings.max_slashing_period,
            consensusRightsDelay: settings.consensus_rights_delay
          }
        })
      );
    }
  }, [settings, dispatch, network]);
  handleError(query.error);
  return query;
};

// src/usePollSaplingBalances.ts
import { useQuery as useQuery8 } from "@tanstack/react-query";
import { RpcClient } from "@taquito/rpc";
import { InMemoryViewingKey, SaplingTransactionViewer } from "@taquito/sapling";
import { RpcReadAdapter } from "@taquito/taquito";
import {
  saplingActions,
  useAppDispatch as useAppDispatch9,
  useCurrentAccount,
  useGetSaplingAccount,
  useSelectedNetwork as useSelectedNetwork7
} from "@umami/state";
import { BLOCK_TIME as BLOCK_TIME4, mutezToTez } from "@umami/tezos";
import { useCustomToast } from "@umami/utils";
import { useEffect as useEffect8, useState } from "react";
var usePollSaplingBalances = () => {
  const dispatch = useAppDispatch9();
  const network = useSelectedNetwork7();
  const refetchTrigger = useRefetchTrigger();
  const toast = useCustomToast();
  const account = useCurrentAccount();
  const accountPkh = account?.address.pkh ?? "";
  const saplingAccount = useGetSaplingAccount(accountPkh);
  const [isFirstTimeToast, setIsFirstTimeToast] = useState(true);
  const query = useQuery8({
    queryKey: ["saplingBalance", network, accountPkh, saplingAccount?.viewingKey, refetchTrigger],
    // eslint-disable-next-line @tanstack/query/no-void-query-fn
    queryFn: async () => {
      const { rpcUrl, saplingContractAddress } = network;
      if (!saplingContractAddress || !accountPkh || !saplingAccount?.viewingKey) {
        return void 0;
      }
      try {
        const viewingKeyProvider = new InMemoryViewingKey(saplingAccount.viewingKey);
        const viewer = new SaplingTransactionViewer(
          viewingKeyProvider,
          { contractAddress: saplingContractAddress },
          new RpcReadAdapter(new RpcClient(rpcUrl))
        );
        const balance2 = await viewer.getBalance();
        const tezBalance = mutezToTez(balance2.toString()).toString();
        setIsFirstTimeToast(true);
        return tezBalance;
      } catch {
        if (isFirstTimeToast) {
          toast({
            description: "Failed to fetch shielded balance. Retrying...",
            status: "error",
            isClosable: true,
            duration: 5e3
          });
          setIsFirstTimeToast(false);
        }
        return saplingAccount.balance || "0";
      }
    },
    retry: false,
    refetchInterval: network.blockTime ?? BLOCK_TIME4,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    enabled: !!accountPkh && !!saplingAccount?.viewingKey && !!network.saplingContractAddress
  });
  const balance = query.data;
  useEffect8(() => {
    if (balance === void 0 || !accountPkh) {
      return;
    }
    dispatch(
      saplingActions.updateAccount({
        id: accountPkh,
        data: { balance }
      })
    );
  }, [dispatch, balance, accountPkh]);
  return query;
};

// src/usePollTokenBalances.ts
import { useQuery as useQuery9 } from "@tanstack/react-query";
import { ChainFullIds as ChainFullIds3 } from "@umami/chains";
import {
  assetsActions as assetsActions5,
  tokensActions,
  useAppDispatch as useAppDispatch10,
  useCurrentAccount as useCurrentAccount2,
  useSelectedNetwork as useSelectedNetwork8
} from "@umami/state";
import { BLOCK_TIME as BLOCK_TIME5 } from "@umami/tezos";
import { getTokenBalances } from "@umami/tzkt";
import { useEffect as useEffect9 } from "react";
var usePollTokenBalances = () => {
  const dispatch = useAppDispatch10();
  const handleError = useReactQueryErrorHandler();
  const network = useSelectedNetwork8();
  const refetchTrigger = useRefetchTrigger();
  const account = useCurrentAccount2();
  const address = account?.chains?.[ChainFullIds3.TEZOS]?.address.pkh ?? "";
  const query = useQuery9({
    queryKey: ["tokenBalances", network, address, refetchTrigger],
    queryFn: () => getTokenBalances(address, network),
    retry: false,
    // retries are handled by the underlying functions
    refetchInterval: network.blockTime ?? BLOCK_TIME5,
    select: (tokenBalances2) => tokenBalances2.flat(),
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false
  });
  const tokenBalances = query.data;
  useEffect9(() => {
    if (!tokenBalances) {
      return;
    }
    const tokens = tokenBalances.map(({ token, lastLevel }) => ({ ...token, lastLevel }));
    dispatch(tokensActions.addTokens({ network, tokens }));
    dispatch(assetsActions5.updateTokenBalance(tokenBalances));
  }, [dispatch, tokenBalances, network]);
  handleError(query.error);
  return query;
};

// src/usePollUnstakeRequests.ts
import { useQuery as useQuery10 } from "@tanstack/react-query";
import { ChainFullIds as ChainFullIds4 } from "@umami/chains";
import { assetsActions as assetsActions6, useAllAccounts as useAllAccounts2, useAppDispatch as useAppDispatch11, useSelectedNetwork as useSelectedNetwork9 } from "@umami/state";
import { BLOCK_TIME as BLOCK_TIME6 } from "@umami/tezos";
import { getPendingUnstakeRequests } from "@umami/tzkt";
import { useEffect as useEffect10 } from "react";
var usePollUnstakeRequests = () => {
  const dispatch = useAppDispatch11();
  const handleError = useReactQueryErrorHandler();
  const network = useSelectedNetwork9();
  const refetchTrigger = useRefetchTrigger();
  const addresses = useAllAccounts2().filter((account) => account.chains?.[ChainFullIds4.TEZOS]).map((account) => account.address.pkh);
  const query = useQuery10({
    queryKey: ["unstakeRequests", dispatch, network, addresses, refetchTrigger],
    queryFn: () => getPendingUnstakeRequests(network, addresses),
    retry: false,
    // retries are handled by the underlying functions
    refetchInterval: network.blockTime ?? BLOCK_TIME6,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false
  });
  const unstakeRequests = query.data;
  useEffect10(() => {
    unstakeRequests && dispatch(assetsActions6.updateUnstakeRequests(unstakeRequests));
  }, [dispatch, unstakeRequests]);
  handleError(query.error);
  return query;
};

// src/useDataPolling.ts
var useDataPolling = () => {
  const dispatch = useAppDispatch12();
  const pollers = [
    usePollAccountStates(),
    usePollBakers(),
    usePollBlock(),
    usePollConversionRate(),
    usePollMultisigs(),
    usePollPendingOperations(),
    usePollProtocolSettings(),
    usePollSaplingBalances(),
    usePollTokenBalances(),
    usePollUnstakeRequests()
  ];
  const isFetching = pollers.some((poller) => poller.isFetching);
  const lastUpdatedAt = max(pollers.map((poller) => poller.dataUpdatedAt));
  useEffect11(() => {
    dispatch(assetsActions7.setIsLoading(isFetching));
    if (!isFetching && lastUpdatedAt) {
      dispatch(
        assetsActions7.setLastTimeUpdated(
          fromUnixTime(Math.round(lastUpdatedAt / 1e3)).toUTCString()
        )
      );
    }
  }, [dispatch, isFetching, lastUpdatedAt]);
  return {
    isFetching
  };
};

// src/useGetOperations.tsx
import {
  useInfiniteQuery,
  useQuery as useQuery11,
  useQueryClient
} from "@tanstack/react-query";
import {
  BLOCK_TIME as BLOCK_TIME7,
  getCombinedOperations,
  useChainRegistry
} from "@umami/chains";
import {
  assetsActions as assetsActions8,
  tokensActions as tokensActions2,
  useAppDispatch as useAppDispatch13,
  useCurrentAccount as useCurrentAccount3,
  useProcessFulfilledData,
  useSelectedNetwork as useSelectedNetwork10
} from "@umami/state";
import { getRelatedTokenTransfers } from "@umami/tzkt";
import { isMultichainEnabled } from "@umami/utils";
import { maxBy } from "lodash";
import { useEffect as useEffect12, useRef } from "react";
var useGetOperations = (account, isEnabled = true) => {
  const network = useSelectedNetwork10();
  const dispatch = useAppDispatch13();
  const refetchTrigger = useRefetchTrigger();
  const handleError = useReactQueryErrorHandler();
  const { txHistory } = useGetTxHistory();
  const queryClient = useQueryClient();
  const triggerRef = useRef(null);
  const {
    isFetching,
    data: operations,
    hasNextPage,
    isLoading,
    fetchNextPage,
    fetchPreviousPage,
    error
  } = useInfiniteQuery({
    queryFn: ({ pageParam }) => fetchOperationsAndUpdateTokensInfo(dispatch, network, account, pageParam),
    queryKey: ["operations", account, dispatch, network],
    initialPageParam: {},
    enabled: isEnabled,
    retry: 3,
    retryDelay: (retryCount) => retryCount * 2e3,
    gcTime: 0,
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage) => {
      if (lastPage.length === 0) {
        return null;
      }
      const lastId = lastPage[lastPage.length - 1].id;
      return { lastId };
    },
    getPreviousPageParam: (_, pages) => {
      const lastId = maxBy(pages.flat(), "id")?.id;
      return lastId ? { lastId, sort: "asc" } : {};
    },
    select: ({ pages }) => filterDuplicatedTokenTransfers(
      [[...pages[0]].sort((a, b) => a.id < b.id ? 1 : -1), ...pages.slice(1)].flat()
    )
  });
  useEffect12(() => {
    if (!operations) {
      return;
    }
    const latestOps = operations.slice(0, 10);
    const hasSafeSendActivity = latestOps.some(
      (op) => op.type === "safe_send" || op.type === "transaction" && op.parameter?.entrypoint && ["claim", "withdraw"].includes(op.parameter.entrypoint)
    );
    if (hasSafeSendActivity) {
      void queryClient.invalidateQueries({
        queryKey: ["operations", account, network],
        refetchType: "active"
      });
    }
  }, [operations, account, network, queryClient]);
  handleError(error);
  useEffect12(() => {
    if (!isEnabled) {
      return;
    }
    const interval = setInterval(() => {
      void fetchPreviousPage();
    }, network.blockTime ?? BLOCK_TIME7);
    return () => clearInterval(interval);
  }, [fetchPreviousPage, isEnabled, network.blockTime]);
  useEffect12(() => {
    if (!isEnabled) {
      return;
    }
    void fetchPreviousPage();
  }, [refetchTrigger, fetchPreviousPage, isEnabled]);
  useEffect12(() => {
    if (!isEnabled || !triggerRef.current) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isLoading) {
          void fetchNextPage();
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0
      }
    );
    observer.observe(triggerRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isLoading, fetchNextPage, isEnabled]);
  let mergedOperations = [];
  if (isEnabled) {
    if (operations) {
      mergedOperations = sortOperations([...operations, ...txHistory]);
    }
  } else {
    mergedOperations = sortOperations(txHistory);
  }
  return {
    hasMore: hasNextPage,
    operations: mergedOperations,
    isFirstLoad: isLoading,
    isLoading: isFetching,
    loadMore: fetchNextPage,
    triggerRef
  };
};
var fetchOperationsAndUpdateTokensInfo = async (dispatch, network, account, options) => {
  const operations = await getCombinedOperations(account.address.pkh, network, options);
  const transactionIds = operations.filter((operation) => operation.type === "transaction").map((operation) => operation.id);
  const tokenTransfersInitiatedByOwnedAccounts = await getRelatedTokenTransfers(
    transactionIds,
    network
  );
  const allTokenTransfersRelatedToOwnedAccounts = operations.filter(
    (operation) => operation.type === "token_transfer"
  );
  const allTokenTransfers = [
    ...tokenTransfersInitiatedByOwnedAccounts,
    ...allTokenTransfersRelatedToOwnedAccounts
  ];
  dispatch(assetsActions8.updateTokenTransfers(allTokenTransfers));
  dispatch(tokensActions2.addTokens({ network, tokens: allTokenTransfers.map((t) => t.token) }));
  return operations;
};
var filterDuplicatedTokenTransfers = (operations) => {
  const transactionIds = new Set(
    operations.filter((op) => op.type !== "token_transfer").map((op) => op.id)
  );
  return operations.filter(
    (op) => op.type !== "token_transfer" || !transactionIds.has(op.transactionId)
  );
};
var useGetTxHistory = () => {
  const { registry } = useChainRegistry();
  const processFulfilledData = useProcessFulfilledData();
  const currentAccount = useCurrentAccount3();
  const network = useSelectedNetwork10();
  const { data: txHistory = [], isLoading } = useQuery11({
    queryKey: ["getTxHistory", currentAccount],
    queryFn: () => registry.getCombinedTransactions(),
    select: processFulfilledData,
    retry: 3,
    refetchInterval: network.blockTime ?? BLOCK_TIME7,
    enabled: isMultichainEnabled()
  });
  return { txHistory, isLoading };
};
var sortOperations = (operations) => operations.sort((a, b) => {
  if (!a.timestamp && !b.timestamp) {
    return 0;
  }
  if (!a.timestamp) {
    return 1;
  }
  if (!b.timestamp) {
    return -1;
  }
  const aTime = new Date(a.timestamp);
  const bTime = new Date(b.timestamp);
  return bTime.getTime() - aTime.getTime();
});
export {
  useDataPolling,
  useGetOperations,
  useGetTxHistory,
  usePollSaplingBalances,
  useReactQueryErrorHandler,
  useRefetchTrigger
};
